import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import chevronDownIcon from '../../icons/Icon-Chevron-Down-Green.svg';
import useLocalize from '../../utils/hooks/useLocalize';

/**
 * Hashtag definitions for the AI Scanner.
 *
 * Each entry is structured to mirror how preset (static) scans work in the
 * project: an `id` that will map to a backend scan configuration, and a
 * `labelKey` for localization. When the real-time wiring (STORY-008) is
 * connected, each hashtag's `id` will be sent to the scan API to retrieve
 * matching results — similar to how `staticScans` are fetched and applied via
 * `state.scans.staticScans` in the existing desktop scanner flow.
 */
export const HASHTAG_DEFINITIONS = [
  { id: 'daily_life_companies', labelKey: 'ai.scanner.hashtag.chip.1' },
  { id: 'elite_management', labelKey: 'ai.scanner.hashtag.chip.2' },
  { id: 'passive_income', labelKey: 'ai.scanner.hashtag.chip.3' },
  { id: 'affordable_stocks', labelKey: 'ai.scanner.hashtag.chip.4' },
  { id: 'bonus_adventure', labelKey: 'ai.scanner.hashtag.chip.5' },
  { id: 'counterattack_from_bottom', labelKey: 'ai.scanner.hashtag.chip.6' },
  { id: 'potential_big_breakout', labelKey: 'ai.scanner.hashtag.chip.7' },
  { id: 'far_from_target_price', labelKey: 'ai.scanner.hashtag.chip.8' },
  { id: 'pro_approved_attention', labelKey: 'ai.scanner.hashtag.chip.9' },
  { id: 'treasure_hunting_undervalued', labelKey: 'ai.scanner.hashtag.chip.10' },
  { id: 'unstoppable_momentum', labelKey: 'ai.scanner.hashtag.chip.11' },
  { id: 'fast_inventory_capital_cycling', labelKey: 'ai.scanner.hashtag.chip.12' },
];

/** Number of chips visible before "show more" is tapped, by viewport height. */
const INITIAL_VISIBLE_SMALL = 6; // height < 667px
const INITIAL_VISIBLE_DEFAULT = 8; // height >= 667px

/**
 * Returns the number of chips to show initially based on viewport height.
 */
const getInitialVisibleCount = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(max-height: 666px)').matches
      ? INITIAL_VISIBLE_SMALL
      : INITIAL_VISIBLE_DEFAULT;
  }
  return INITIAL_VISIBLE_DEFAULT;
};

/**
 * HashtagChips Component
 *
 * Renders a grid of selectable hashtag pill-shaped chips inside the AI Scanner
 * bottom sheet. Chips toggle between selected (green) and unselected (grey)
 * states on tap.
 *
 * Initially shows 6 chips (<667px screens) or 8 chips (≥667px screens).
 * Tapping "さらに表示" reveals the remaining chips.
 *
 * The action bar at the bottom provides "clear all" and "show results" controls.
 * Live match-count filtering is deferred to STORY-008; a static placeholder
 * count ("--- 件") is shown for now.
 *
 * @param {Object} props
 * @param {Function} [props.onShowResults] - Called when the "show results" button is pressed.
 *   Will be wired in STORY-008. Receives the array of selected hashtag IDs.
 * @param {Function} [props.onSelectionChange] - Called whenever the set of
 *   selected hashtag IDs changes (toggle or clear). Receives the new Set of IDs.
 *   Used by AiScannerContainer to fire de-duplicated scan requests (STORY-007).
 * @param {number|null} [props.matchCount] - STORY-008: live count of matching
 *   symbols for the current selection. `null` indicates no count is available
 *   yet (loading or error) and renders the "---" placeholder.
 * @param {boolean} [props.isLoading] - STORY-008: true while any required scan
 *   is still being fetched.
 * @param {boolean} [props.hasError] - STORY-008: true when at least one required
 *   scan has errored; disables the submit button.
 */
const HashtagChips = ({
  onShowResults,
  onSelectionChange,
  matchCount = null,
  isLoading = false,
  hasError = false
}) => {
  const localize = useLocalize();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(getInitialVisibleCount);

  // Update visible count on viewport height changes (e.g. rotation)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(max-height: 666px)');
    const handleChange = (e) => {
      if (!expanded) {
        setVisibleCount(e.matches ? INITIAL_VISIBLE_SMALL : INITIAL_VISIBLE_DEFAULT);
      }
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [expanded]);

  const handleChipToggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
    onSelectionChange?.(new Set(next));
  };

  const handleClearAll = () => {
    setSelectedIds(new Set());
    onSelectionChange?.(new Set());
  };

  const handleShowResults = () => {
    if (onShowResults) {
      onShowResults(Array.from(selectedIds));
    }
  };

  const handleShowMore = useCallback(() => {
    setExpanded(true);
  }, []);

  const chipsToRender = expanded
    ? HASHTAG_DEFINITIONS
    : HASHTAG_DEFINITIONS.slice(0, visibleCount);

  const hasMoreChips = !expanded && visibleCount < HASHTAG_DEFINITIONS.length;

  return (
    <div className="hashtag-chips">
      <div className="hashtag-chips-scroll">
        <div className="hashtag-chips-grid" role="group" aria-label={localize('ai.scanner.hashtag.modal.title')}>
          {chipsToRender.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`hashtag-chip${selectedIds.has(tag.id) ? ' hashtag-chip--selected' : ''}`}
              onClick={() => handleChipToggle(tag.id)}
              aria-pressed={selectedIds.has(tag.id)}
            >
              {localize(tag.labelKey)}
            </button>
          ))}
        </div>
        {hasMoreChips && (
          <button
            type="button"
            className="hashtag-chips-show-more"
            aria-expanded={expanded}
            onClick={handleShowMore}
          >
            <span className="hashtag-chips-show-more-text">
              {localize('ai.scanner.hashtag.show.more')}
            </span>
            <img
              src={chevronDownIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
              className="hashtag-chips-show-more-chevron"
            />
          </button>
        )}
      </div>
      <div className="hashtag-chips-action-bar">
        <button
          type="button"
          className="hashtag-chips-action-bar-clear"
          onClick={handleClearAll}
          disabled={selectedIds.size === 0}
        >
          {localize('ai.scanner.hashtag.clear.all')}
        </button>
        <button
          type="button"
          className="hashtag-chips-action-bar-submit"
          onClick={handleShowResults}
          disabled={
            selectedIds.size === 0 || isLoading || hasError || matchCount === 0
          }
        >
          {localize('ai.scanner.hashtag.show.results', {
            0:
              selectedIds.size === 0 || matchCount == null
                ? '---'
                : String(matchCount)
          })}
        </button>
      </div>
    </div>
  );
};

HashtagChips.propTypes = {
  onShowResults: PropTypes.func,
  onSelectionChange: PropTypes.func,
  matchCount: PropTypes.number,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool
};

export default HashtagChips;
